/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { upperFirst } from 'lodash';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { clearAlertToasts, toast } from '../paToast';
import { getSupportTicketUrl } from '../support';
import { getToastThemeVars } from '../toastTheme';
import AlertDetailsDialog from './AlertDetailsDialog';
import AlertHistoryPanel from './AlertHistoryPanel';
import {
  closeRetainedToastDetails,
  dismissRetainedToast,
  getToastHistorySnapshot,
  minimizeRetainedToast,
  openRetainedToastDetails,
  type RetainedToast,
  subscribeToToastHistory,
} from './toastHistoryStore';

/** Alerts tray: timed-out warnings/errors collapse into a pill bottom-left; the panel lists them. */
export default function ToastHistory() {
  const theme = useTheme();
  const location = useLocation();
  const { records, detailsId } = useSyncExternalStore(
    subscribeToToastHistory,
    getToastHistorySnapshot,
    getToastHistorySnapshot,
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const alertHistoryRef = useRef<HTMLDivElement>(null);
  const detailsContentRef = useRef<HTMLElement>(null);
  const routeRef = useRef(location.pathname);
  const minimizedToasts = records.filter(({ minimized }) => minimized);
  const detailsToast = records.find(({ id }) => id === detailsId) ?? null;
  const hasError = minimizedToasts.some(({ type }) => type === 'error');
  let PillIcon = hasError ? ErrorOutlineRoundedIcon : WarningAmberRoundedIcon;
  if (isExpanded) PillIcon = CloseRoundedIcon;

  useEffect(
    () =>
      toast.onChange(({ id, reason, status, type }) => {
        if (status !== 'removed' || (type !== 'error' && type !== 'warning')) return;
        if (reason) {
          dismissRetainedToast(id);
        } else {
          minimizeRetainedToast(id);
        }
      }),
    [],
  );

  useEffect(() => {
    const route = location.pathname;
    if (routeRef.current === route) return;

    routeRef.current = route;
    clearAlertToasts();
    setIsExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const collapseOnClickAway = (event: MouseEvent) => {
      if (!alertHistoryRef.current?.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', collapseOnClickAway);
    return () => document.removeEventListener('mousedown', collapseOnClickAway);
  }, [isExpanded]);

  const dismiss = (id: string | number) => {
    if (minimizedToasts.length === 1) {
      setIsExpanded(false);
    }
    dismissRetainedToast(id);
  };

  const clear = () => {
    clearAlertToasts();
    setIsExpanded(false);
  };

  const getToastContent = useCallback((retained: RetainedToast) => {
    return typeof retained.content === 'string'
      ? retained.content
      : (detailsContentRef.current?.innerText ??
          detailsContentRef.current?.textContent ??
          retained.summary);
  }, []);

  return (
    <>
      {minimizedToasts.length > 0 && (
        <Stack
          className="theme"
          ref={alertHistoryRef}
          // The tray renders outside App's toast root, so it carries the toast colours itself.
          style={getToastThemeVars(theme)}
          spacing={1}
          sx={{
            alignItems: 'flex-start',
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: (theme) => theme.zIndex.modal - 1,
          }}
        >
          {isExpanded && (
            <AlertHistoryPanel
              toasts={minimizedToasts}
              onOpen={(id) => {
                openRetainedToastDetails(id);
                setIsExpanded(false);
              }}
              onDismiss={dismiss}
              onClear={clear}
            />
          )}
          <ButtonBase
            aria-controls={isExpanded ? 'alert-history' : undefined}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Open'} warning and error history`}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            sx={{
              gap: 0.75,
              pl: 1.25,
              pr: 1.5,
              py: 0.75,
              borderRadius: 999,
              border: '1px solid var(--pa-toast-border)',
              bgcolor: 'var(--pa-toast-surface)',
              boxShadow: 'var(--pa-toast-shadow)',
              backdropFilter: 'blur(20px) saturate(160%)',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <PillIcon
              sx={{
                fontSize: 16,
                color: isExpanded ? 'text.secondary' : `${hasError ? 'error' : 'warning'}.main`,
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {minimizedToasts.length === 1 ? '1 alert' : `${minimizedToasts.length} alerts`}
            </Typography>
          </ButtonBase>
        </Stack>
      )}

      <AlertDetailsDialog
        toast={detailsToast}
        contentRef={detailsContentRef}
        onClose={closeRetainedToastDetails}
        onCopy={async () => {
          if (!detailsToast) return;
          try {
            await navigator.clipboard.writeText(getToastContent(detailsToast));
            toast.success('Copied to clipboard');
          } catch {
            toast.error('Could not copy to clipboard');
          }
        }}
        onReport={() => {
          if (!detailsToast) return;
          const message = getToastContent(detailsToast);
          window.open(getSupportTicketUrl(`${upperFirst(detailsToast.type)}:\n\n${message}`));
        }}
      />
    </>
  );
}
